import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlVdcDocumentsRemove, urlVdcDocumentsUpload } from '../../../Constants';
import { IAction, SliceProps, SlicePropsWithData } from '../../store';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IVdcCollaborationState = {
    loading: false,
    error: null,
    vdcViewerUploadState: {
        data: null,
        loading: false,
        error: null,
    },
    vdcViewerRemoveState: {
        data: null,
        loading: false,
        error: null,
    },
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    resetVdcViewerUpload: (state: Draft<IVdcCollaborationState>) => {
        state.vdcViewerUploadState.data = null;
    },
    resetLoadingVdcViewerUpload: (state: Draft<IVdcCollaborationState>) => {
        state.vdcViewerUploadState.loading = false;
    },
    resetErrorVdcViewerUpload: (state: Draft<IVdcCollaborationState>) => {
        state.vdcViewerUploadState.error = null;
    },

    resetVdcViewerRemove: (state: Draft<IVdcCollaborationState>) => {
        state.vdcViewerRemoveState.data = null;
    },
    resetLoadingVdcViewerRemove: (state: Draft<IVdcCollaborationState>) => {
        state.vdcViewerRemoveState.loading = false;
    },
    resetErrorVdcViewerRemove: (state: Draft<IVdcCollaborationState>) => {
        state.vdcViewerRemoveState.error = null;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that uploads document(s) to VDC Collaboration.
     * @param config - Object that contains the document information to know which file needs should be opened.
     * @returns Promise for the HTTP request.
     */
    uploadVdcCollaborationDocuments: async (config: {
        replicaId: string;
        documentIds: string[];
    }): Promise<IVdcProjectConnectionInfo> => {
        try {
            const { replicaId, documentIds } = config;

            const formData = new FormData();
            formData.append('documentIds', JSON.stringify(documentIds));

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlVdcDocumentsUpload, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            const repsonseContent = await response.json();

            return Promise.resolve(repsonseContent);
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that removes document(s) from VDC Collaboration.
     * @param config - Object that contains the document information to know which file needs should be opened.
     * @returns Promise for the HTTP request.
     */
    removeVdcCollaborationDocuments: async (config: {
        replicaId: string;
        documentIds: string[];
    }): Promise<IVdcProjectConnectionInfo> => {
        try {
            const { replicaId, documentIds } = config;

            const formData = new FormData();
            formData.append('documentIds', JSON.stringify(documentIds));

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                },
                body: formData,
            };

            const response = await apiFetch(urlVdcDocumentsRemove, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            const repsonseContent = await response.json();

            return Promise.resolve(repsonseContent);
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<SliceProps>) => {
        /**
         * Reducers for the 'uploadVdcViewerDocument' thunk.
         */
        builder.addCase(uploadVdcViewerDocument.pending, (state: Draft<IVdcCollaborationState>) => {
            state.vdcViewerUploadState.loading = true;
            state.vdcViewerUploadState.error = null;
        });
        builder.addCase(
            uploadVdcViewerDocument.fulfilled,
            (state: Draft<IVdcCollaborationState>, action: IAction<IVdcProjectConnectionInfo>) => {
                state.vdcViewerUploadState.loading = false;
                state.vdcViewerUploadState.error = null;
                state.vdcViewerUploadState.data = action.payload;
            },
        );
        builder.addCase(
            uploadVdcViewerDocument.rejected,
            (state: Draft<IVdcCollaborationState>, action: IAction<SerializedError>) => {
                state.vdcViewerUploadState.loading = false;
                state.vdcViewerUploadState.error = action.error;
            },
        );

        /**
         * Reducers for the 'removeVdcViewerDocument' thunk.
         */
        builder.addCase(removeVdcViewerDocument.pending, (state: Draft<IVdcCollaborationState>) => {
            state.vdcViewerRemoveState.loading = true;
            state.vdcViewerRemoveState.error = null;
        });
        builder.addCase(
            removeVdcViewerDocument.fulfilled,
            (state: Draft<IVdcCollaborationState>, action: IAction<IVdcProjectConnectionInfo>) => {
                state.vdcViewerRemoveState.loading = false;
                state.vdcViewerRemoveState.error = null;
                state.vdcViewerRemoveState.data = action.payload;
            },
        );
        builder.addCase(
            removeVdcViewerDocument.rejected,
            (state: Draft<IVdcCollaborationState>, action: IAction<SerializedError>) => {
                state.vdcViewerRemoveState.loading = false;
                state.vdcViewerRemoveState.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const uploadVdcViewerDocument = createAsyncThunk(
    'document/uploadVdcViewerDocument',
    thunks.uploadVdcCollaborationDocuments,
);
/**
 * Expose thunk functions as exports.
 */
export const removeVdcViewerDocument = createAsyncThunk(
    'document/removeVdcViewerDocument',
    thunks.removeVdcCollaborationDocuments,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IVdcCollaborationState extends SliceProps {
    /** Slice props of vdc viewer document upload. */
    vdcViewerUploadState: SlicePropsWithData<IVdcProjectConnectionInfo>;

    /** Slice props of vdc viewer document remove. */
    vdcViewerRemoveState: SlicePropsWithData<IVdcProjectConnectionInfo>;
}

export interface IVdcProjectConnectionInfo {
    /** The list of documents that failed to be uploaded/removed. */
    failedDocuments: string[];
}

/**
 * Create the redux slice.
 */
export const vdcCollaboration = createSlice({
    name: 'vdcCollaboration',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    resetVdcViewerUpload,
    resetLoadingVdcViewerUpload,
    resetErrorVdcViewerUpload,
    resetVdcViewerRemove,
    resetLoadingVdcViewerRemove,
    resetErrorVdcViewerRemove,
} = vdcCollaboration.actions;

/**
 * Export slice reducer as default.
 */
export default vdcCollaboration.reducer;
